/**
 * Find Your Size v2 - Complete Redesign
 * Smart matching algorithm with accessibility focus
 */

(function() {
  'use strict';

  window.FindYourSize = {
    // Configuration
    config: {
      maxResults: 12,
      priceRangePct: 0.3,  // ±30% price range for matching
      cacheTimeout: 300000, // 5 minutes
      searchLimit: 100     // Max products to fetch
    },

    // State
    state: {
      isOpen: false,
      currentProduct: null,
      selectedSize: null,
      triggerElement: null
    },

    // Cache for API responses
    cache: new Map(),

    /**
     * Initialize the module
     */
    init: function() {
      this.cacheElements();
      this.bindEvents();
    },

    /**
     * Cache DOM elements
     */
    cacheElements: function() {
      this.modal = document.querySelector('.find-size-modal');
      if (!this.modal) return;

      this.backdrop = this.modal.querySelector('.find-size-modal__backdrop');
      this.container = this.modal.querySelector('.find-size-modal__container');
      this.closeBtn = this.modal.querySelector('.find-size-modal__close');
      this.sizesContainer = this.modal.querySelector('[data-size-buttons]');
      this.resultsContainer = this.modal.querySelector('[data-results]');
    },

    /**
     * Bind event listeners
     */
    bindEvents: function() {
      var self = this;

      // Open triggers
      document.addEventListener('click', function(e) {
        var trigger = e.target.closest('.js-find-size-open');
        if (trigger) {
          e.preventDefault();
          self.openFromTrigger(trigger);
        }
      });

      // Close handlers
      if (this.backdrop) {
        this.backdrop.addEventListener('click', function() {
          self.close();
        });
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function() {
          self.close();
        });
      }

      // Escape key to close
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.state.isOpen) {
          self.close();
        }
      });
    },

    /**
     * Open modal from trigger element
     */
    openFromTrigger: function(trigger) {
      var wrapper = trigger.closest('.find-size-trigger, .find-your-size-module');
      if (!wrapper) return;

      var productData = {
        id: wrapper.dataset.productId,
        handle: wrapper.dataset.productHandle,
        title: wrapper.dataset.productTitle || '',
        baseName: wrapper.dataset.baseName || '',
        type: wrapper.dataset.productType || '',
        tags: (wrapper.dataset.productTags || wrapper.dataset.tags || '').split(',').filter(Boolean),
        gender: wrapper.dataset.gender || 'unknown',
        price: parseInt(wrapper.dataset.price || wrapper.dataset.productPrice || '0', 10),
        collection: wrapper.dataset.productCollection || '',
        unavailableSizes: (wrapper.dataset.unavailableSizes || '').split(',').filter(Boolean)
      };

      this.state.triggerElement = trigger;
      this.state.currentProduct = productData;
      this.open(productData);
    },

    /**
     * Open the modal
     */
    open: function(productData) {
      if (!this.modal) {
        this.cacheElements();
        if (!this.modal) return;
      }

      this.state.isOpen = true;
      this.state.currentProduct = productData;
      this.state.selectedSize = null;

      // Render size buttons
      this.renderSizes(productData);

      // Show prompt state
      this.showPrompt();

      // Show modal - remove hidden attribute and inline style first
      this.modal.removeAttribute('hidden');
      this.modal.style.display = '';
      this.modal.classList.add('find-size-modal--open');
      document.body.style.overflow = 'hidden';

      // Setup focus trap
      this.setupFocusTrap();

      // Focus first size button
      var firstSize = this.sizesContainer.querySelector('.find-size-modal__size');
      if (firstSize) {
        setTimeout(function() {
          firstSize.focus();
        }, 100);
      }
    },

    /**
     * Close the modal
     */
    close: function() {
      if (!this.modal) return;

      this.state.isOpen = false;
      this.modal.classList.remove('find-size-modal--open');
      document.body.style.overflow = '';

      // Re-hide modal after transition
      setTimeout(function() {
        if (!this.state.isOpen && this.modal) {
          this.modal.setAttribute('hidden', '');
          this.modal.style.display = 'none';
        }
      }.bind(this), 300);

      // Return focus to trigger
      if (this.state.triggerElement) {
        this.state.triggerElement.focus();
      }
    },

    /**
     * Setup focus trap for accessibility
     */
    setupFocusTrap: function() {
      var self = this;
      var focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

      this.modal.addEventListener('keydown', function(e) {
        if (e.key !== 'Tab') return;

        var focusable = self.container.querySelectorAll(focusableSelector);
        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    },

    /**
     * Render size buttons (using actual unavailable sizes from product)
     */
    renderSizes: function(productData) {
      if (!this.sizesContainer) return;

      var self = this;
      var sizeArray = productData.unavailableSizes || [];

      // If no unavailable sizes found, show message
      if (sizeArray.length === 0) {
        this.sizesContainer.innerHTML = '<p class="find-size-modal__no-sizes">No unavailable sizes found.</p>';
        return;
      }

      var html = '<span class="find-size-modal__sizes-label">Select your size</span>';
      html += '<div class="find-size-modal__sizes-grid">';

      sizeArray.forEach(function(size) {
        html += '<button type="button" class="find-size-modal__size" data-size="' +
                self.escapeHtml(size) + '">' + self.escapeHtml(size) + '</button>';
      });

      html += '</div>';
      this.sizesContainer.innerHTML = html;

      // Bind size button clicks
      this.sizesContainer.querySelectorAll('.find-size-modal__size').forEach(function(btn) {
        btn.addEventListener('click', function() {
          self.selectSize(this.dataset.size);
        });
      });
    },

    /**
     * Handle size selection
     */
    selectSize: function(size) {
      this.state.selectedSize = size;

      // Update button states
      this.sizesContainer.querySelectorAll('.find-size-modal__size').forEach(function(btn) {
        btn.classList.toggle('find-size-modal__size--selected', btn.dataset.size === size);
      });

      // Find alternatives
      this.findAlternatives(this.state.currentProduct, size);
    },

    /**
     * Show initial prompt state
     */
    showPrompt: function() {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML =
        '<div class="find-size-modal__prompt">' +
          '<svg class="find-size-modal__prompt-icon" viewBox="0 0 24 24" fill="none">' +
            '<path d="M21 21H3V3" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
            '<path d="M21 7V3H17" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
            '<path d="M7 21V17" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
            '<path d="M11 21V19" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
            '<path d="M15 21V17" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
          '</svg>' +
          '<p class="find-size-modal__prompt-text">Select a size above to see alternatives</p>' +
        '</div>';
    },

    /**
     * Show loading state
     */
    showLoading: function() {
      if (!this.resultsContainer) return;

      var html = '<div class="find-size-modal__loading">' +
        '<p class="find-size-modal__loading-text">Finding alternatives...</p>' +
        '<div class="find-size-modal__skeleton-grid">';

      for (var i = 0; i < 6; i++) {
        html += '<div class="find-size-modal__skeleton-card">' +
          '<div class="find-size-modal__skeleton-image"></div>' +
          '<div class="find-size-modal__skeleton-title"></div>' +
          '<div class="find-size-modal__skeleton-price"></div>' +
        '</div>';
      }

      html += '</div></div>';
      this.resultsContainer.innerHTML = html;
    },

    /**
     * Show empty state
     */
    showEmpty: function() {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML =
        '<div class="find-size-modal__empty">' +
          '<svg class="find-size-modal__empty-icon" viewBox="0 0 24 24" fill="none">' +
            '<circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.5"/>' +
            '<path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
            '<path d="M8 11H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '</svg>' +
          '<h3 class="find-size-modal__empty-title">No alternatives found</h3>' +
          '<p class="find-size-modal__empty-text">We couldn\'t find similar products in this size. Check back soon for restocks.</p>' +
        '</div>';
    },

    /**
     * Find alternatives with smart matching
     */
    findAlternatives: function(productData, size) {
      var self = this;

      this.showLoading();

      // Check cache first
      var cacheKey = this.getCacheKey(productData, size);
      var cached = this.cache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < this.config.cacheTimeout) {
        this.renderResults(cached.data, size);
        return;
      }

      // Fetch products
      this.fetchProducts(productData)
        .then(function(products) {
          var scored = self.scoreAndRank(products, productData, size);

          // Cache results
          self.cache.set(cacheKey, {
            data: scored,
            timestamp: Date.now()
          });

          self.renderResults(scored, size);
        })
        .catch(function(error) {
          console.error('Find Your Size error:', error);
          self.showEmpty();
        });
    },

    /**
     * Generate cache key
     */
    getCacheKey: function(productData, size) {
      return productData.gender + '-' + size + '-' + (productData.type || 'all');
    },

    /**
     * Fetch products from Shopify
     */
    fetchProducts: function(productData) {
      var self = this;

      // Try collection first, then fall back to search
      if (productData.collection) {
        return this.fetchFromCollection(productData.collection, productData)
          .then(function(products) {
            if (products.length < 6) {
              // Supplement with search results
              return self.fetchFromSearch(productData).then(function(searchResults) {
                return self.mergeProducts(products, searchResults);
              });
            }
            return products;
          });
      }

      return this.fetchFromSearch(productData);
    },

    /**
     * Fetch from collection
     */
    fetchFromCollection: function(collectionHandle, productData) {
      var self = this;
      var url = '/collections/' + encodeURIComponent(collectionHandle) + '/products.json?limit=' + this.config.searchLimit;

      return fetch(url)
        .then(function(response) {
          if (!response.ok) throw new Error('Collection not found');
          return response.json();
        })
        .then(function(data) {
          return self.normalizeProducts(data.products || [], productData);
        })
        .catch(function() {
          return [];
        });
    },

    /**
     * Fetch from predictive search
     */
    fetchFromSearch: function(productData) {
      var self = this;
      var query = productData.type || productData.baseName || 'shoes';
      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product&resources[limit]=' + this.config.searchLimit;

      return fetch(url)
        .then(function(response) {
          if (!response.ok) throw new Error('Search failed');
          return response.json();
        })
        .then(function(data) {
          var products = data.resources && data.resources.results && data.resources.results.products;
          return self.normalizeProducts(products || [], productData);
        })
        .catch(function() {
          return [];
        });
    },

    /**
     * Normalize products from different API formats
     */
    normalizeProducts: function(products, sourceProduct) {
      var self = this;

      return products
        .filter(function(p) {
          // Exclude current product
          return String(p.id) !== String(sourceProduct.id);
        })
        .map(function(p) {
          return {
            id: p.id,
            handle: p.handle,
            title: p.title,
            type: p.product_type || p.type || '',
            tags: Array.isArray(p.tags) ? p.tags : (p.tags || '').split(',').map(function(t) { return t.trim(); }),
            price: self.getPrice(p),
            image: self.getImage(p),
            variants: p.variants || [],
            url: '/products/' + p.handle
          };
        });
    },

    /**
     * Get product price (returns as number)
     */
    getPrice: function(product) {
      if (product.price) {
        return typeof product.price === 'number' ? product.price : parseFloat(product.price);
      }
      if (product.variants && product.variants[0]) {
        return parseFloat(product.variants[0].price);
      }
      return 0;
    },

    /**
     * Get product image
     */
    getImage: function(product) {
      if (product.featured_image) {
        return typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image.url;
      }
      if (product.image) {
        return typeof product.image === 'string' ? product.image : product.image.src;
      }
      if (product.images && product.images[0]) {
        return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
      }
      return '';
    },

    /**
     * Merge products removing duplicates
     */
    mergeProducts: function(products1, products2) {
      var seen = {};
      var merged = [];

      products1.concat(products2).forEach(function(p) {
        if (!seen[p.id]) {
          seen[p.id] = true;
          merged.push(p);
        }
      });

      return merged;
    },

    /**
     * Score and rank products
     */
    scoreAndRank: function(products, sourceProduct, size) {
      var self = this;

      return products
        .filter(function(p) {
          return self.hasSize(p, size);
        })
        .map(function(p) {
          return {
            product: p,
            score: self.calculateScore(p, sourceProduct)
          };
        })
        .sort(function(a, b) {
          return b.score - a.score;
        })
        .slice(0, this.config.maxResults)
        .map(function(item) {
          return item.product;
        });
    },

    /**
     * Check if product has size available
     */
    hasSize: function(product, size) {
      if (!product.variants || !product.variants.length) return true; // Assume yes if no variant data

      return product.variants.some(function(v) {
        // Check if variant has this size and is available
        var optionMatch = (v.option1 === size || v.option2 === size || v.option3 === size);
        var titleMatch = v.title && v.title.includes(size);
        return (optionMatch || titleMatch) && v.available !== false;
      });
    },

    /**
     * Calculate match score (0-100)
     */
    calculateScore: function(product, source) {
      var score = 0;

      // Price similarity (40 points max)
      if (source.price > 0 && product.price > 0) {
        var priceDiff = Math.abs(product.price - source.price) / source.price;
        score += Math.max(0, 40 - (priceDiff * 100));
      } else {
        score += 20; // Default if no price data
      }

      // Same product type (25 points)
      if (source.type && product.type) {
        if (product.type.toLowerCase() === source.type.toLowerCase()) {
          score += 25;
        }
      }

      // Style/tag matching (20 points max, 5 per matching tag)
      var sharedTags = this.countSharedTags(product, source);
      score += Math.min(20, sharedTags * 5);

      // Same gender (15 points)
      var productGender = this.detectGender(product);
      var sourceGender = source.gender;
      if (productGender === sourceGender) {
        score += 15;
      }

      return Math.round(score);
    },

    /**
     * Count shared tags between products
     */
    countSharedTags: function(product, source) {
      if (!product.tags || !source.tags) return 0;

      var sourceTags = source.tags.map(function(t) { return t.toLowerCase(); });
      var productTags = product.tags.map(function(t) { return t.toLowerCase(); });

      return productTags.filter(function(tag) {
        return sourceTags.indexOf(tag) !== -1;
      }).length;
    },

    /**
     * Detect gender from product
     */
    detectGender: function(product) {
      var tags = product.tags || [];
      var tagsStr = tags.join(',').toLowerCase();

      if (tagsStr.includes('mfw') || tagsStr.includes('men')) return 'men';
      if (tagsStr.includes('wfw') || tagsStr.includes('women')) return 'women';
      return 'unknown';
    },

    /**
     * Render results
     */
    renderResults: function(products, size) {
      if (!this.resultsContainer) return;

      if (!products || products.length === 0) {
        this.showEmpty();
        return;
      }

      var self = this;
      var html = '<div class="find-size-modal__results-header">' +
        '<span class="find-size-modal__results-count">' + products.length + ' alternative' +
        (products.length !== 1 ? 's' : '') + ' found in size ' + this.escapeHtml(size) + '</span>' +
      '</div>';

      html += '<div class="find-size-modal__results-grid">';

      products.forEach(function(product) {
        html += self.renderProductCard(product, size);
      });

      html += '</div>';
      this.resultsContainer.innerHTML = html;
    },

    /**
     * Render a product card (safe from XSS)
     */
    renderProductCard: function(product, size) {
      var imageUrl = product.image || '';
      // Resize image for performance
      if (imageUrl && imageUrl.includes('cdn.shopify.com')) {
        imageUrl = imageUrl.replace(/\.([^.]+)$/, '_400x400.$1');
      }

      return '<a href="' + this.escapeHtml(product.url) + '" class="find-size-card">' +
        '<div class="find-size-card__image-wrap">' +
          (imageUrl ? '<img class="find-size-card__image" src="' + this.escapeHtml(imageUrl) + '" alt="' + this.escapeHtml(product.title) + '" loading="lazy">' : '') +
        '</div>' +
        '<div class="find-size-card__content">' +
          '<h3 class="find-size-card__title">' + this.escapeHtml(product.title) + '</h3>' +
          '<span class="find-size-card__price">' + this.formatPrice(product.price) + '</span>' +
          '<div class="find-size-card__size-available">Size ' + this.escapeHtml(size) + ' available</div>' +
        '</div>' +
      '</a>';
    },

    /**
     * Format price
     */
    formatPrice: function(price) {
      if (!price) return '';
      // Shopify products.json returns prices as strings (e.g., "5999.00")
      // Parse and format without dividing by 100
      var amount = parseFloat(price);
      if (isNaN(amount)) return '';
      return 'EGP ' + Math.round(amount).toLocaleString('en-EG');
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml: function(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window.FindYourSize.init();
    });
  } else {
    window.FindYourSize.init();
  }
})();
